import { useChangedRecently, useEffectOnValueChange } from '@timelyapp/react';
import classNames from 'classnames';
import AvatarStack from 'components/AvatarStack';
import { Checkmark } from 'components/Icons';
import Label from 'components/Label';
import { useDragHandle } from 'hooks/useDragHandle';
import { clamp } from 'lodash-es';
import React, { useCallback, useMemo, useState } from 'react';
import { useDrag } from 'react-dnd';
import FontAwesome from 'react-fontawesome';
import { isContrastReadable, mixColors, prefixHexColor } from 'utils/colors';
import { fromISO } from 'utils/luxon-helpers';
import { pick } from 'utils/typesafe';
import styles from './GanttChart.module.scss';
import type { GanttTaskDragObject, GanttTaskDropResult } from './gantt-dnd';
import { GANTTT_TASK_DND_TYPE } from './gantt-dnd';
import type { GanttLayoutMeta, GanttTaskWithLayout } from './gantt-internal-types';
import type {
  GanttRow,
  GanttTask,
  GanttTaskCopyParameters,
  GanttTooltipComponent,
} from './gantt-types';

const TASK_PADDING = Number(styles.taskPadding);
const CHECKMARK_OFFSET_WIDTH = Number(styles.checkmarkOffsetWidth);

export interface Props {
  taskWithLayout: GanttTaskWithLayout;
  ganttRow: GanttRow;
  layoutMeta: GanttLayoutMeta;
  scrollLeft: number;
  scrollAreaWidth: number;
  onDragStop: () => void;
  onUpdateResizePreview: (
    task: GanttTask,
    newDates: null | { startDate: string; endDate: string }
  ) => void;
  onClickTask: (task: GanttTask, row: GanttRow) => void;

  onMoveTask: (
    task: GanttTask,
    row: GanttRow,
    toDates: { startDate: string; endDate: string },
    meta: { interaction: 'move' | 'resize' }
  ) => void;
  onReassignTask: (task: GanttTask, from: GanttRow, to: GanttRow) => void;
  onCopyTask: (task: GanttTask, parameters: GanttTaskCopyParameters) => void;
  tooltip: GanttTooltipComponent;
  disableTooltip: boolean;
  allowChangeTask: boolean;
}

const GanttChartTask: React.FC<Props> = props => {
  const {
    allowChangeTask,
    taskWithLayout,
    ganttRow,
    layoutMeta,
    scrollLeft,
    scrollAreaWidth,
    disableTooltip,
    onDragStop,
    onUpdateResizePreview,
    onClickTask,
    onMoveTask,
    onReassignTask,
    onCopyTask,
  } = props;
  const { task, ...position } = taskWithLayout;

  const [isHovering, setIsHovering] = useState(false);
  const handleMouseEnter = useCallback(() => {
    setIsHovering(true);
  }, []);
  const handleMouseLeave = useCallback(() => {
    setIsHovering(false);
  }, []);

  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: GANTTT_TASK_DND_TYPE,
      item: (): GanttTaskDragObject => {
        return {
          task,
          fromRow: ganttRow,
          initialScrollLeft: scrollLeft,
        };
      },
      canDrag: () => allowChangeTask,
      collect: monitor => ({
        isDragging: monitor.isDragging(),
      }),
      end: (item: GanttTaskDragObject, monitor) => {
        const dropObject = monitor.getDropResult<GanttTaskDropResult>();
        if (!dropObject) {
          onDragStop();
          return;
        }
        const { toRow, dropEffect, move } = dropObject;
        const getRowId = (row: GanttRow) => (row.type === 'user' ? row.userId : row.projectId);
        if (dropEffect === 'copy') {
          onCopyTask(task, { fromRow: item.fromRow, toRow, withDates: move });
        } else if (getRowId(item.fromRow) === getRowId(toRow)) {
          onMoveTask(item.task, ganttRow, move, { interaction: 'move' });
          return undefined;
        } else {
          // We'll probably not want to reassign and move later on, with drag layers and stuff
          onReassignTask(
            {
              ...item.task,
              ...move,
            },
            item.fromRow,
            toRow
          );
        }

        onDragStop();
      },
      previewOptions: {
        anchorX: 0, // Make sure preview matches hover offset
      },
    }),
    [
      task,
      onDragStop,
      scrollLeft,
      ganttRow,
      onMoveTask,
      onReassignTask,
      onCopyTask,
      allowChangeTask,
    ]
  );

  const calculateResize = useCallback(
    (delta: number, direction: 'left' | 'right') => {
      const deltaColumns = Math.round(delta / layoutMeta.zoom.columnWidth);
      const offsetStart = direction === 'left' ? deltaColumns : 0;
      const offsetEnd = direction === 'right' ? deltaColumns : 0;
      const layoutStartDate = fromISO(layoutMeta.startDate);

      let startDate = fromISO(task.startDate).plus({ days: offsetStart });
      let endDate = fromISO(task.endDate).plus({ days: offsetEnd });
      if (startDate > endDate) {
        // Prevent resizing past end in either direction
        if (direction === 'left') {
          startDate = endDate;
        } else {
          endDate = startDate;
        }
      }
      const left = startDate.diff(layoutStartDate, 'days').days * layoutMeta.zoom.columnWidth;
      const width =
        (endDate.diff(startDate, 'days').days + 1) * layoutMeta.zoom.columnWidth -
        layoutMeta.zoom.dayPadding;

      return {
        left,
        width,
        startDate: startDate.toISODate(),
        endDate: endDate.toISODate(),
      };
    },
    [layoutMeta, task.startDate, task.endDate]
  );

  const handleResize = useCallback(
    (delta: number, direction: 'left' | 'right') => {
      onMoveTask(task, ganttRow, pick(calculateResize(delta, direction), 'startDate', 'endDate'), {
        interaction: 'resize',
      });
    },
    [onMoveTask, task, ganttRow, calculateResize]
  );
  const handleResizeRight = useCallback(
    (delta: number) => {
      handleResize(delta, 'right');
    },
    [handleResize]
  );
  const handleResizeLeft = useCallback(
    (delta: number) => {
      handleResize(delta, 'left');
    },
    [handleResize]
  );
  const [dragHandleRightRef, resizingDeltaRight] = useDragHandle(handleResizeRight);
  const [dragHandleLeftRef, resizingDeltaLeft] = useDragHandle(handleResizeLeft);

  const displayColor = prefixHexColor(
    task.type === 'user'
      ? task.displayColor
      : ganttRow.type === 'project'
        ? ganttRow.displayColor
        : '333333'
  );
  const style: React.CSSProperties = {
    background: displayColor,
    ...position,
  };

  const resizingDelta = resizingDeltaRight ?? resizingDeltaLeft;
  const resizing = resizingDelta != null;
  const resizingDirection = resizing && resizingDeltaLeft != null ? 'left' : 'right';
  let resizeShadow: React.ReactElement | null = null;
  let resizePreviewDays: null | { startDate: string; endDate: string } = null;
  if (resizingDelta != null) {
    const { width, left, startDate, endDate } = calculateResize(resizingDelta, resizingDirection);
    style.left = left;
    style.width = width;
    resizePreviewDays = { startDate, endDate };

    // Leave a shadow of the old size behind the task
    resizeShadow = (
      <div
        className={classNames(styles.task, styles.resizeShadow)}
        style={{
          background: displayColor,
          ...position,
        }}
      />
    );
  }

  useEffectOnValueChange(resizePreviewDays, () => {
    onUpdateResizePreview(task, resizePreviewDays);
  });

  const [titleWidth, setTitleWidth] = useState(0);
  const titleRef = useCallback(
    (element: HTMLDivElement | null) => {
      setTitleWidth(element?.offsetWidth ?? 0);
    },
    // Want to re-evaluate when title changes
    // biome-ignore lint/correctness/useExhaustiveDependencies: intentional
    [setTitleWidth, task.title]
  );

  const recentlyResized = useChangedRecently(resizing, 300);
  const handleClick = useCallback(() => {
    if (!recentlyResized) {
      onClickTask(task, ganttRow);
    }
  }, [onClickTask, task, ganttRow, recentlyResized]);

  const hideText = position.width < (task.completed ? CHECKMARK_OFFSET_WIDTH : 0) + 50;
  const hideCompleteCheckbox = position.width < TASK_PADDING + CHECKMARK_OFFSET_WIDTH;

  const contrastReadable = useMemo(() => isContrastReadable('#fff', displayColor), [displayColor]);

  let progressBackground: React.ReactElement | null = null;
  if (!task.completed && task.progress != null && task.progress > 0) {
    const progressPercent = clamp(Math.round(task.progress), 0, 100);
    const mixColor = contrastReadable ? 'ffffff' : '000000';
    const mixAmount = contrastReadable ? 60 : 30;

    // We want the progress bar to always be fully visible,
    // even if the task is partially outside the viewport
    const progressOffsetLeft = Math.max(0, scrollLeft - position.left);
    const progressOffsetRight = Math.max(
      0,
      position.left + position.width - (scrollLeft + scrollAreaWidth)
    );

    progressBackground = (
      <div
        className={styles.progressBarWrapper}
        style={{
          left: progressOffsetLeft,
          right: progressOffsetRight,
          height: position.height / 12,
        }}
      >
        <div
          className={styles.progressBar}
          style={{
            background: prefixHexColor(mixColors(displayColor, mixColor, mixAmount)),
            width: `${progressPercent}%`,
          }}
        />
      </div>
    );
  }

  const tooltipElement = useMemo(
    () => (
      <props.tooltip id={task.id} viewContext={ganttRow.type}>
        <div />
      </props.tooltip>
    ),
    [ganttRow.type, props, task.id]
  );

  return (
    <>
      {resizeShadow}
      <div
        ref={drag}
        onClick={handleClick}
        className={classNames(styles.task, styles.animated, {
          [styles.resizing]: resizing,
          [styles.dragging]: isDragging,
          [styles.completed]: task.completed,
          [styles.darkText]: !contrastReadable,
        })}
        style={style}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onMouseDown={handleMouseLeave}
      >
        {isHovering && !disableTooltip && (
          <div
            className={styles.taskTooltipReference}
            style={{
              // By having the edges of this div stay inside the viewport, the tooltip will
              // never be off screen.
              left: clamp(scrollLeft - position.left, 0, position.width),
              right: clamp(
                position.width - (scrollLeft - position.left) - scrollAreaWidth,
                0,
                position.width
              ),
            }}
          >
            {tooltipElement}
          </div>
        )}
        {progressBackground}
        <div
          className={styles.taskContent}
          data-testid="task-content"
          style={{
            // Make title sticky when scrolling horizontally
            paddingLeft: clamp(
              scrollLeft -
                position.left +
                // Make sure it's not too close to the edge
                TASK_PADDING,
              // Never set to less than the default padding
              TASK_PADDING,
              // The maximum padding is the width of the task minus the width of the title
              position.width -
                titleWidth -
                // We need to make sure we don't go too close to the right
                // edge - subtract the default padding
                TASK_PADDING -
                // When we display the checkmark, we need to account for
                // the offset (width+/marginpadding) width of the checkmark
                (task.completed ? CHECKMARK_OFFSET_WIDTH : 0)
            ),
          }}
        >
          {task.completed && !hideCompleteCheckbox && (
            <div className={styles.checkmark}>
              <Checkmark />
            </div>
          )}
          {!task.completed && !hideCompleteCheckbox && task.type === 'project' && (
            <div className={styles.avatar}>
              <AvatarStack
                users={task.users.map(u => ({
                  name: u.userName,
                  avatar: { medium_retina: u.avatar },
                }))}
                size="small"
              />
            </div>
          )}
          {!hideText && (
            <div className={styles.text}>
              <div className={styles.title}>
                <span ref={titleRef} data-testid="task-title" className="fs-mask">
                  {task.title}
                </span>
              </div>
              {task.type === 'user' && (
                <div className={styles.project}>
                  {task.projectName}
                  {task.projectArchived && (
                    <Label className={styles.archived}>
                      <FontAwesome name="archive" />
                    </Label>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
        {position.width >= TASK_PADDING && allowChangeTask && (
          <>
            <div
              className={classNames(styles.dragHandle, styles.dragHandleLeft)}
              ref={dragHandleLeftRef}
            />
            <div className={styles.dragHandle} ref={dragHandleRightRef} />
          </>
        )}
      </div>
    </>
  );
};
export default GanttChartTask;
